import React from 'react'
import PropTypes from 'prop-types'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import { translate } from 'react-i18next'
import { updateUserInformation } from './../../actions/AuthAction'
import * as translationService from './../../services/translationService'
import * as routeService from './../../services/routeService'
import * as networkService from './../../services/networkService'
import * as userService from './../../services/userService'
import { validateProfile } from './../../utils/validation'
import { cleanApiErrorMessage, getApiErrorMessage } from './../../utils/apiError'
import {
  Stack,
  TextField,
  MobileNumber,
  ButtonGroup,
  Button,
  DisplayText,
  TextContainer,
  ErrorText,
} from './../../components'
import { dump } from '../../services/debugService'
import { parsePhoneNumber } from '../../utils/phone'

const AccountView = ({ user, updateUserInformation }) => {
  const [person, setPerson] = React.useState({})
  const [showResendVerificationMail, setShowResendVerificationMail] = React.useState(false)
  const [fieldErrors, setFieldErrors] = React.useState({})
  const [informationIsUpdated, setInformationIsUpdated] = React.useState(false)
  const [generalError, setGeneralError] = React.useState()
  const [isSaving, setIsSaving] = React.useState(false)

  React.useEffect(
    () => {
      const { firstname, lastname, phone, email, unverifiedEmail, unverifiedPhone } = user

      const person = {
        firstname,
        lastname,
        phone: unverifiedPhone || phone,
        email: unverifiedEmail || email,
      }

      if (person.email !== user.email) {
        setShowResendVerificationMail(true)
      }

      setPerson(person)
    },
    [user],
  )

  const hasUnverifiedEmail = person.email && person.email.replace(/\s+/g, '').toLowerCase() !== user.email
  const hasUnverifiedPhone = person.phone && person.phone.replace(/([^0-9|+|/])/g, '').toLowerCase() !== user.phone

  // `hasUnverified*` compares what is currently typed, so it flips on the first keystroke -- it
  // decides what the payload carries, which is correct there, but it cannot drive the
  // "(unverified)" label, which must describe what is actually stored on the user.
  const hasStoredUnverifiedEmail = !!user.unverifiedEmail
  const hasStoredUnverifiedPhone = !!user.unverifiedPhone

  const onChange = ({ target: { name, value } }) => {
    setPerson({
      ...person,
      [name]: value,
    })
  }

  const handleUserDelete = async () => {
    const deleteUser = confirm(translationService.deleteAccountConfirm())
    if (deleteUser) {
      try {
        await networkService.removeApplications()
        await networkService.removeProfile()
        userService.logOutUser()
      } catch (err) {
        setGeneralError(translationService.deleteAccountFailed())
      }
    }
  }

  const savePersonInformation = async event => {
    event.preventDefault()

    setInformationIsUpdated(false)
    setFieldErrors({})
    setGeneralError(undefined)
    setIsSaving(true)

    const personToSave = { firstname: person.firstname, lastname: person.lastname }

    const personEmail = person.email ? person.email.replace(/\s+/g, '').toLowerCase() : undefined
    const personPhone = person.phone ? person.phone.replace(/([^0-9|+|/])/g, '').toLowerCase() : undefined

    if (hasUnverifiedEmail || (user.unverifiedEmail && user.unverifiedEmail !== personEmail)) {
      personToSave.unverifiedEmail = personEmail
      setShowResendVerificationMail(true)
    }

    if (hasUnverifiedPhone || (user.unverifiedPhone && user.unverifiedPhone !== personPhone)) {
      personToSave.unverifiedPhone = personPhone
    }

    Object.keys(personToSave).forEach(key => personToSave[key] == null && delete personToSave[key])

    try {
      await validateProfile(personToSave)
      personToSave.unverifiedPhone = parsePhoneNumber(personPhone)
      const { success, data: updatedPerson } = await updateUserInformation(personToSave)
      if (success) {
        const { unverifiedPhone, unverifiedEmail, unverifiedPhoneIsVerifiable } = updatedPerson
        if (unverifiedPhone && unverifiedPhoneIsVerifiable) {
          routeService.gotToPhoneNumberVerificationPage({ redirectBack: true })
        }

        if (unverifiedEmail && unverifiedPhone && !unverifiedPhoneIsVerifiable) {
          routeService.goToEmailVerificationPage({ redirectBack: true })
        }
        setInformationIsUpdated(true)
      }
    } catch (error) {
      dump({ error })

      setShowResendVerificationMail(false)

      // `validateProfile` rejects with yup's plain { path: message } map, which is already keyed
      // the way the fields below read it. Everything the thunk rejects with carries a
      // `serverError`, so its absence is what tells the two apart.
      if (!error.serverError) {
        setFieldErrors(error)
        return
      }

      // One general message for every API failure: no per-field routing, and a message that is
      // not plain user-facing copy is replaced by the generic one. `getApiErrorMessage` also
      // returns undefined when there is no response body at all (a network failure or a timeout),
      // which the same fallback covers.
      const message = getApiErrorMessage(error.serverError)
      setGeneralError(cleanApiErrorMessage(message) || translationService.generalError())
    } finally {
      setIsSaving(false)
    }
  }

  const resendVerification = async () => {
    setGeneralError(undefined)

    try {
      await networkService.resendVerificationMail(person.email)
      setShowResendVerificationMail(false)
    } catch (error) {
      // This call does not go through the thunk, so `error` is the raw axios error. Feeding it to
      // `setFieldErrors` rendered nothing and wiped whatever field errors were on screen, so the
      // failure was invisible.
      dump({ error })
      setInformationIsUpdated(false)
      const message = getApiErrorMessage(error)
      setGeneralError(cleanApiErrorMessage(message) || translationService.generalError())
    }
  }

  const getEmailHelpText = () => {
    if (hasUnverifiedEmail && showResendVerificationMail) {
      return (
        <React.Fragment>
          {translationService.profileHistoryInfoEmailHelpText()}{' '}
          <Button onClick={resendVerification} plain>
            {translationService.profileResendVerificationMail()}
          </Button>
        </React.Fragment>
      )
    }
  }

  return (
    <form onSubmit={savePersonInformation}>
      <TextContainer>
        {informationIsUpdated && (
          <DisplayText size="extraSmall">{translationService.t('overview.profile.information.updated')}</DisplayText>
        )}
        {generalError && <ErrorText>{generalError}</ErrorText>}
        <Stack distribution="fill">
          <TextField
            label={translationService.t('system.firstname')}
            id="firstname"
            name="firstname"
            value={person.firstname}
            onChange={onChange}
            error={fieldErrors && fieldErrors.firstname}
          />
          <TextField
            label={translationService.t('system.lastname')}
            id="lastname"
            name="lastname"
            value={person.lastname}
            onChange={onChange}
            error={fieldErrors && fieldErrors.lastname}
          />
        </Stack>
        <TextField
          label={
            translationService.t('system.email') +
            (hasStoredUnverifiedEmail ? ` ${translationService.profileHistoryInfoUnverifiedText()}` : '')
          }
          id="email"
          name="email"
          helpText={getEmailHelpText()}
          value={person.email}
          onChange={onChange}
          error={fieldErrors && fieldErrors.unverifiedEmail}
        />
        <MobileNumber
          label={
            translationService.t('system.mobile_number') +
            (hasStoredUnverifiedPhone ? ` ${translationService.profileHistoryInfoUnverifiedText()}` : '')
          }
          id="phone"
          name="phone"
          value={person.phone}
          onChange={onChange}
          error={fieldErrors && fieldErrors.unverifiedPhone}
        />
        <ButtonGroup>
          <Button submit primary loading={isSaving}>
            {translationService.buttonSave()}
          </Button>
          <Button plain error onClick={handleUserDelete}>
            {translationService.deleteAccountButton()}
          </Button>
        </ButtonGroup>
      </TextContainer>
    </form>
  )
}

AccountView.propTypes = {
  user: PropTypes.object,
  updateUserInformation: PropTypes.func,
}

const mapStateToProps = ({ AuthReducer }) => {
  const { user } = AuthReducer
  return {
    user: user || {},
  }
}
const mapDispatchToProps = dispatch => ({
  updateUserInformation: userInformation => dispatch(updateUserInformation(userInformation)),
})
export default compose(
  translate('translations'),
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(AccountView)



// WEBPACK FOOTER //
// ./src/views/Profile/Account.js